/**
 * PowerSync Upload Handler
 * 
 * Этап 3: Реализация uploadCrudEntry для маппинга CrudEntry → API вызовы.
 * 
 * Когда PowerSync обнаруживает локальные изменения (INSERT/UPDATE/DELETE),
 * он вызывает uploadData в коннекторе, который обрабатывает каждый CrudEntry
 * через эту функцию.
 * 
 * CrudEntry содержит:
 * - table: имя таблицы
 * - op: тип операции (1=PUT/create, 2=PATCH/update, 3=DELETE)
 * - id: ID записи (UUID для локальных, integer-as-string для серверных)
 * - opData: данные операции (значения колонок)
 */

import type { CrudEntry } from '@powersync/common';
import { UpdateType } from '@powersync/common';

/**
 * Проверяет, является ли ID строки UUID (локально созданная запись).
 * Серверные ID — числовые (integer-as-string).
 */
function isUUID(id: string): boolean {
  return /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(id);
}

export interface UploadApiClient {
  // Transactions
  addTransaction(data: any): Promise<any>;
  updateTransaction(id: number, data: any): Promise<any>;
  deleteTransaction(id: number, type: string): Promise<any>;
  // Categories
  addCategory(name: string, options?: { parent_id?: number; emoji?: string; category_level?: string }): Promise<any>;
  renameCategory(oldName: string, newName: string): Promise<any>;
  renameCategoryById?(categoryId: string | number, newName: string, parentId?: number | null, emoji?: string): Promise<any>;
  deleteCategory(name: string, targetCategoryId?: number): Promise<any>;
  deleteCategoryById?(categoryId: number | string, targetCategoryId?: number | string): Promise<any>;
  // Goals
  addGoal(goal: any): Promise<any>;
  updateGoal(goalId: string, updates: any): Promise<any>;
  deleteGoal(goalId: string): Promise<any>;
  // One-time Goal Deposits (expenses with is_monthly=2)
  depositToGoal?(goalId: string, amount: number, paymentMethodId?: string): Promise<any>;
  // Recurring Expense API (expenses with is_monthly NOT IN (0, 2))
  addRecurringExpense?(data: any): Promise<any>;
  updateRecurringExpense?(id: number, data: any): Promise<any>;
  deleteRecurringExpense?(id: number, withHistory?: boolean): Promise<any>;
  toggleRecurringExpense?(id: number): Promise<any>;
  // Limits
  setLimit(category: string, amount: number, periodMonths?: number, categoryId?: number, startMonth?: string): Promise<any>;
  deleteLimit(category: string, periodMonths: number): Promise<any>;
  // Recurring Income
  addRecurringIncomeDefinition?(data: any): Promise<any>;
  updateRecurringIncomeDefinition?(id: number, data: any): Promise<any>;
  deleteRecurringIncomeDefinition?(id: number, withHistory?: boolean): Promise<any>;
  toggleRecurringIncomeDefinition?(id: number): Promise<any>;
  // Recurring Goal Deposit Templates
  createRecurringGoalDeposit?(data: any): Promise<any>;
  updateRecurringGoalDeposit?(id: string, data: any): Promise<any>;
  deleteRecurringGoalDeposit?(id: string): Promise<any>;
  toggleRecurringGoalDeposit?(id: string): Promise<any>;
  // Plaid
  updatePlaidTransactionCategory?(transactionId: string, category: string): Promise<any>;
  renamePlaidAccount?(accountDbId: number, customName: string | null): Promise<any>;
  // Payment Methods
  createPaymentMethod?(name: string, type: string, balance: number, subtype?: string): Promise<any>;
  renamePaymentMethod?(id: string, name: string): Promise<any>;
  updatePaymentMethodType?(id: string, subtype: string, name: string): Promise<any>;
  updatePaymentMethodBalance?(id: string, balance: number): Promise<any>;
  setDefaultPaymentMethod?(id: string, isDefault: boolean): Promise<any>;
  deletePaymentMethod?(id: string, targetAccountId?: string): Promise<any>;
  // Close / Restore Goal
  closeGoal?(goalId: string, paymentMethodId?: string): Promise<any>;
  restoreGoal?(goalId: string): Promise<any>;
  // User Settings
  updateSettings?(settings: Record<string, any>): Promise<any>;
  // User Category Colors
  upsertCategoryColor?(id: string, categoryId: string, color: string): Promise<any>;
  deleteCategoryColor?(categoryId: string): Promise<any>;
}

/**
 * Создаёт обработчик uploadCrudEntry с привязкой к apiClient.
 * @param apiClient - API клиент для серверных вызовов
 * @param getDb - опциональная функция для получения локальной PowerSync БД
 *                (нужна для чтения полных данных строки, когда PATCH opData неполный)
 * @param getTimezone - опциональная функция для получения текущего IANA timezone устройства
 */
export function createUploadHandler(apiClient: UploadApiClient, getDb?: () => any, getTimezone?: () => string) {
  return async function uploadCrudEntry(entry: CrudEntry): Promise<void> {
    const { table, op } = entry;
    const id = entry.id;
    const data = entry.opData || {};

    console.log(`[PowerSync Upload] ${getOpName(op)} on ${table} id=${id}`);

    switch (table) {
      case 'expenses':
        await handleExpenses(apiClient, op, id, data, getDb, getTimezone);
        break;

      case 'income_transactions':
        await handleIncomeTransactions(apiClient, op, id, data, getDb);
        break;

      case 'categories':
        await handleCategories(apiClient, op, id, data);
        break;

      case 'goals':
        await handleGoals(apiClient, op, id, data);
        break;

      case 'category_limits':
        await handleCategoryLimits(apiClient, op, id, data, getDb);
        break;

      case 'plaid_transactions':
        await handlePlaidTransactions(apiClient, op, id, data, getDb);
        break;

      case 'payment_methods':
        await handlePaymentMethods(apiClient, op, id, data);
        break;

      case 'plaid_accounts':
        await handlePlaidAccounts(apiClient, op, id, data);
        break;

      case 'recurring_income_definitions':
        await handleRecurringIncomeDefinitions(apiClient, op, id, data, getTimezone);
        break;

      case 'goal_deposits':
        await handleGoalDepositTemplates(apiClient, op, id, data);
        break;

      case 'user_settings':
        await handleUserSettings(apiClient, op, id, data);
        break;

      case 'user_category_colors':
        await handleUserCategoryColors(apiClient, op, id, data);
        break;

      default:
        console.warn(`[PowerSync Upload] Unknown table: ${table}`);
    }
  };
}

function getOpName(op: UpdateType): string {
  switch (op) {
    case UpdateType.PUT: return 'CREATE';
    case UpdateType.PATCH: return 'UPDATE';
    case UpdateType.DELETE: return 'DELETE';
    default: return `UNKNOWN(${op})`;
  }
}

// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
// 📊 Expenses (обрабатывает все типы: обычные, recurring, goal deposits)
// is_monthly: 0=one-time (или yearly с бэкенда + month), 1=monthly recurring,
//             2=goal deposit, 3=weekly recurring, 4=yearly recurring (локальное)
// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━

/**
 * Конвертирует is_monthly обратно в expense_type для API.
 * 1 → 'monthly', 3 → 'weekly', 4 → 'yearly', 0+month → 'yearly' (бэкенд)
 */
function isMonthlyToExpenseType(isMonthly: number, month: any): string {
  if (isMonthly === 3) return 'weekly';
  if (isMonthly === 1) return 'monthly';
  if (isMonthly === 4) return 'yearly';
  return 'yearly'; // is_monthly === 0 с month (данные с бэкенда)
}

/**
 * Определяет, является ли расход регулярным.
 * is_monthly: 1=monthly, 3=weekly, 4=yearly (локальное), 0+month=yearly (бэкенд)
 */
function isExpenseRecurring(isMonthly: number, month: any): boolean {
  if (isMonthly === 1 || isMonthly === 3 || isMonthly === 4) return true;
  if (isMonthly === 0 && month != null) return true; // yearly from backend
  return false;
}

async function handleExpenses(
  apiClient: UploadApiClient,
  op: UpdateType,
  id: string,
  data: Record<string, any>,
  getDb?: () => any,
  getTimezone?: () => string
): Promise<void> {
  let isMonthly = Number(data.is_monthly);
  console.log(`[RECURRING][UPLOAD_EXPENSE] op=${op} id=${id} is_monthly_from_opData=${data.is_monthly} parsed=${isMonthly}`);

  // Для PATCH/DELETE — is_monthly может отсутствовать в opData.
  // Читаем полную строку из локальной БД для определения типа.
  if ((op === UpdateType.PATCH || op === UpdateType.DELETE) && (isNaN(isMonthly) || data.is_monthly === undefined) && getDb) {
    try {
      const db = getDb();
      const rows = await db.getAll('SELECT is_monthly, goal_id, month FROM expenses WHERE id = ?', [id]);
      if (rows?.[0]) {
        isMonthly = Number(rows[0].is_monthly) || 0;
        if (data.goal_id === undefined && rows[0].goal_id) data.goal_id = rows[0].goal_id;
        if (data.month === undefined && rows[0].month != null) data.month = rows[0].month;
        console.log(`[RECURRING][UPLOAD_EXPENSE] Read from DB: is_monthly=${isMonthly} goal_id=${rows[0].goal_id} month=${rows[0].month}`);
      } else {
        isMonthly = isNaN(isMonthly) ? 0 : isMonthly;
        console.log(`[RECURRING][UPLOAD_EXPENSE] No row found in DB for id=${id}, fallback is_monthly=${isMonthly}`);
      }
    } catch (e) {
      console.warn('[PowerSync Upload] Failed to read expense row for type detection:', e);
      isMonthly = isNaN(isMonthly) ? 0 : isMonthly;
    }
  } else {
    isMonthly = isNaN(isMonthly) ? 0 : isMonthly;
  }

  const isGoalDeposit = isMonthly === 2 && data.goal_id;
  const isRecurring = isExpenseRecurring(isMonthly, data.month);
  console.log(`[RECURRING][UPLOAD_EXPENSE] Routing: is_monthly=${isMonthly} month=${data.month} isRecurring=${isRecurring} isGoalDeposit=${isGoalDeposit}`);

  if (op === UpdateType.PUT) {
    if (isGoalDeposit) {
      // ── Goal Deposit: вызываем depositToGoal() ──
      if (!apiClient.depositToGoal) {
        console.warn('[PowerSync Upload] depositToGoal not implemented');
        return;
      }
      await apiClient.depositToGoal(
        String(data.goal_id),
        Number(data.amount),
        data.payment_method_id ? String(data.payment_method_id) : undefined
      );
    } else if (isRecurring) {
      // ── Recurring Expense: вызываем addRecurringExpense() ──
      if (!apiClient.addRecurringExpense) {
        console.warn('[RECURRING][UPLOAD_EXPENSE] addRecurringExpense not implemented');
        return;
      }
      const expenseType = isMonthlyToExpenseType(isMonthly, data.month);
      console.log(`[RECURRING][UPLOAD_EXPENSE] PUT → API addRecurringExpense: title="${data.title}" type=${expenseType} is_monthly=${isMonthly} start=${data.start_date_recurring} tz=${getTimezone?.() || 'UTC'}`);
      await apiClient.addRecurringExpense({
        title: data.title,
        amount: Number(data.amount),
        category: data.category,
        category_id: data.category_id ? Number(data.category_id) : undefined,
        expense_type: expenseType,
        start_date_recurring: data.start_date_recurring,
        notification_time: data.notification_time,
        timezone: getTimezone?.() || 'UTC',
        disable_payment_notifications: !!data.disable_payment_notifications,
        payment_method_id: data.payment_method_id ? Number(data.payment_method_id) : undefined,
        goal_id: data.goal_id ? Number(data.goal_id) : undefined,
        reminder_times: safeJsonParse(data.reminder_times) || [],
        reminder_offsets: safeJsonParse(data.reminder_offsets) || [0],
        reminders_enabled: !!data.reminders_enabled,
        description: data.description,
        next_notification_date: data.next_notification_date || undefined,
      });
      console.log(`[RECURRING][UPLOAD_EXPENSE] ✅ PUT completed for id=${id}`);
    } else {
      // ── Regular one-time expense ──
      await apiClient.addTransaction({
        type: 'expense',
        title: data.title,
        amount: Number(data.amount),
        category: data.category,
        category_id: data.category_id ? Number(data.category_id) : undefined,
        date: data.payment_date,
        description: data.description,
        payment_method_id: data.payment_method_id ? String(data.payment_method_id) : undefined,
        is_internal_transfer: !!data.is_internal_transfer,
        goal_id: data.goal_id ? Number(data.goal_id) : undefined,
        receipt_image: data.receipt_image,
        receipt_items: data.receipt_items ? safeJsonParse(data.receipt_items) : undefined,
        receipt_tax: data.receipt_tax ? Number(data.receipt_tax) : undefined,
        receipt_tip: data.receipt_tip ? Number(data.receipt_tip) : undefined,
        receipt_service_fee: data.receipt_service_fee ? Number(data.receipt_service_fee) : undefined,
        receipt_discount: data.receipt_discount ? Number(data.receipt_discount) : undefined,
        input_method: data.input_method,
        is_monthly: Number(data.is_monthly) || 0,
        // Для дочерних транзакций от recurring — привязка к родителю (для "удалить с историей")
        recurring_expense_id: data.recurring_expense_id ? Number(data.recurring_expense_id) : undefined,
        // Offline-first: мобильный процессор уже обновил баланс локально,
        // CRUD PATCH для payment_methods принесёт корректное абсолютное значение.
        // Сервер не должен повторно списывать.
        skip_balance_update: data.input_method === 'local_processor',
      });
    }
  } else if (op === UpdateType.PATCH) {
    if (isUUID(id)) {
      console.log('[PowerSync Upload] Skipping UPDATE for UUID expense (not yet synced):', id);
      return;
    }
    const serverId = parseInt(id);
    if (isNaN(serverId)) {
      console.warn('[PowerSync Upload] Cannot update expense with non-numeric id:', id);
      return;
    }

    if (isRecurring) {
      // ── Recurring Expense UPDATE ──
      if (!apiClient.updateRecurringExpense) return;
      const updates: Record<string, any> = {};
      if (data.title !== undefined) updates.title = data.title;
      if (data.amount !== undefined) updates.amount = Number(data.amount);
      if (data.category !== undefined) updates.category = data.category;
      if (data.category_id !== undefined) updates.category_id = Number(data.category_id);
      if (data.is_monthly !== undefined) updates.expense_type = isMonthlyToExpenseType(Number(data.is_monthly), data.month);
      if (data.start_date_recurring !== undefined) {
        // Сервер ожидает "YYYY-MM-DD", а процессор хранит полный timestamp "2026-03-15 14:36:00Z"
        updates.start_date_recurring = String(data.start_date_recurring).replace(/[T ].+$/, '');
        // API требует expense_type вместе с start_date_recurring (для expense_details).
        // Если expense_type не в PATCH — дочитываем is_monthly из БД
        if (!updates.expense_type && getDb) {
          try {
            const dbRow = await getDb().getOptional(
              'SELECT is_monthly, month FROM expenses WHERE id = ?', [id]
            );
            if (dbRow) {
              updates.expense_type = isMonthlyToExpenseType(Number(dbRow.is_monthly), dbRow.month);
            }
          } catch {}
        }
      }
      if (data.notification_time !== undefined) updates.notification_time = data.notification_time;
      if (data.reminder_times !== undefined) updates.reminder_times = safeJsonParse(data.reminder_times);
      if (data.reminder_offsets !== undefined) updates.reminder_offsets = safeJsonParse(data.reminder_offsets);
      if (data.reminders_enabled !== undefined) updates.reminders_enabled = !!data.reminders_enabled;
      if (data.payment_method_id !== undefined) updates.payment_method_id = Number(data.payment_method_id);
      if (data.is_active !== undefined) updates.is_active = !!data.is_active;
      // next_notification_date: мобильный — авторитет, отправляем чтобы сервер хранил актуальное значение
      if (data.next_notification_date !== undefined) updates.next_notification_date = data.next_notification_date;

      // Если нет полей для обновления, пропускаем API вызов
      if (Object.keys(updates).length === 0) {
        console.log(`[RECURRING][UPLOAD_EXPENSE] PATCH → Пропускаем: нет полей для отправки на сервер (serverId=${serverId})`);
        return;
      }
      console.log(`[RECURRING][UPLOAD_EXPENSE] PATCH → API updateRecurringExpense serverId=${serverId} fields=[${Object.keys(updates).join(', ')}]`);
      await apiClient.updateRecurringExpense(serverId, updates);
      console.log(`[RECURRING][UPLOAD_EXPENSE] ✅ PATCH completed for serverId=${serverId}`);
    } else {
      // ── Regular expense UPDATE ──
      // PowerSync PATCH содержит только ИЗМЕНЁННЫЕ поля (напр. только amount при правке суммы).
      // Сервер при PUT перезаписывает все поля — если не передать title/category/payment_method_id,
      // они станут дефолтными (Расход/Разное/null). Дочитываем полную строку из БД при неполном PATCH.
      let updateData = cleanUpdateData(data);
      const needsEnrichment =
        !updateData.title || !updateData.amount || !updateData.date || !updateData.category || updateData.payment_method_id === undefined;
      if (needsEnrichment && getDb) {
        try {
          const db = getDb!();
          const rows = await db.getAll('SELECT * FROM expenses WHERE id = ?', [id]);
          if (rows?.[0]) {
            const row = rows[0];
            updateData = {
              ...updateData,
              title: updateData.title ?? row.title,
              amount: updateData.amount !== undefined ? Number(updateData.amount) : Number(row.amount),
              date: updateData.date || updateData.payment_date || row.payment_date,
              category: updateData.category ?? row.category,
              category_id: updateData.category_id ?? row.category_id,
              description: updateData.description ?? row.description,
              payment_method_id: updateData.payment_method_id !== undefined
                ? (updateData.payment_method_id ? String(updateData.payment_method_id) : undefined)
                : (row.payment_method_id ? String(row.payment_method_id) : undefined),
              is_internal_transfer: updateData.is_internal_transfer !== undefined ? !!updateData.is_internal_transfer : !!row.is_internal_transfer,
            };
            console.log(`[PowerSync Upload] Expense ${id}: enriched partial PATCH with full row data`);
          }
        } catch (e) {
          console.warn('[PowerSync Upload] Failed to read full expense row from DB:', e);
        }
      }
      await apiClient.updateTransaction(serverId, {
        type: 'expense',
        ...updateData,
      });
    }
  } else if (op === UpdateType.DELETE) {
    if (isUUID(id)) {
      console.log('[PowerSync Upload] Skipping DELETE for UUID expense (not yet synced):', id);
      return;
    }
    const serverId = parseInt(id);
    if (isNaN(serverId)) {
      console.warn('[PowerSync Upload] Cannot delete expense with non-numeric id:', id);
      return;
    }

    if (isRecurring) {
      if (!apiClient.deleteRecurringExpense) return;
      console.log(`[RECURRING][UPLOAD_EXPENSE] DELETE → API deleteRecurringExpense serverId=${serverId}`);
      await apiClient.deleteRecurringExpense(serverId, false);
      console.log(`[RECURRING][UPLOAD_EXPENSE] ✅ DELETE completed for serverId=${serverId}`);
    } else if (isGoalDeposit) {
      // Goal deposit expenses: отправляем DELETE на сервер для корректного ревёрта суммы и статуса цели
      console.log('[PowerSync Upload] Deleting goal deposit expense on server:', id);
      await apiClient.deleteTransaction(serverId, 'expense');
    } else {
      // Если is_monthly не определён (opData пуст при DELETE), пробуем оба API
      // сначала recurring, потом обычный
      if (isMonthly === 0 && data.month == null && !data.goal_id) {
        // Неизвестный тип — пробуем как обычный расход
        await apiClient.deleteTransaction(serverId, 'expense');
      } else {
        await apiClient.deleteTransaction(serverId, 'expense');
      }
    }
  }
}

// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
// 💰 Income Transactions
// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
async function handleIncomeTransactions(
  apiClient: UploadApiClient,
  op: UpdateType,
  id: string,
  data: Record<string, any>,
  getDb?: () => any
): Promise<void> {
  if (op === UpdateType.PUT) {
    await apiClient.addTransaction({
      type: 'income',
      title: data.title,
      amount: Number(data.amount),
      category: data.category || undefined,
      category_id: data.category_id ? Number(data.category_id) : undefined,
      date: data.transaction_date,
      description: data.description,
      payment_method_id: data.payment_method_id ? String(data.payment_method_id) : undefined,
      is_internal_transfer: !!data.is_internal_transfer,
      input_method: data.input_method,
      recurring_income_id: data.recurring_income_id ? Number(data.recurring_income_id) : undefined,
      skip_balance_update: data.input_method === 'local_processor',
    });
  } else if (op === UpdateType.PATCH) {
    if (isUUID(id)) {
      console.log('[PowerSync Upload] Skipping UPDATE for UUID income (not yet synced):', id);
      return;
    }
    const serverId = parseInt(id);
    if (isNaN(serverId)) {
      console.warn('[PowerSync Upload] Cannot update income with non-numeric id:', id);
      return;
    }
    let updateData = cleanUpdateData(data);
    // Маппинг: PowerSync хранит transaction_date, а сервер ожидает date
    if (updateData.transaction_date && !updateData.date) {
      updateData.date = updateData.transaction_date;
      delete updateData.transaction_date;
    }
    // Защита от NaN в category_id
    if (updateData.category_id !== undefined) {
      const catId = Number(updateData.category_id);
      updateData.category_id = isNaN(catId) ? undefined : catId;
    }
    // PowerSync PATCH содержит только изменённые поля. Сервер при PUT перезаписывает всё.
    // Дочитываем полную строку из БД при неполном PATCH (отсутствует title, amount, date, category или payment_method_id).
    const needsEnrichment =
      !updateData.title || !updateData.amount || !updateData.date || !updateData.category || updateData.payment_method_id === undefined;
    if (needsEnrichment && getDb) {
      try {
        const db = getDb();
        const rows = await db.getAll('SELECT * FROM income_transactions WHERE id = ?', [id]);
        if (rows?.[0]) {
          const row = rows[0];
          updateData = {
            ...updateData,
            title: updateData.title ?? row.title,
            amount: updateData.amount !== undefined ? Number(updateData.amount) : Number(row.amount),
            date: updateData.date || updateData.transaction_date || row.transaction_date,
            category: updateData.category ?? row.category,
            category_id: updateData.category_id ?? (row.category_id ? Number(row.category_id) : undefined),
            description: updateData.description ?? row.description,
            payment_method_id: updateData.payment_method_id !== undefined
              ? (updateData.payment_method_id ? String(updateData.payment_method_id) : undefined)
              : (row.payment_method_id ? String(row.payment_method_id) : undefined),
            is_internal_transfer: updateData.is_internal_transfer !== undefined ? !!updateData.is_internal_transfer : !!row.is_internal_transfer,
          };
          if (updateData.transaction_date && !updateData.date) {
            updateData.date = updateData.transaction_date;
          }
          delete updateData.transaction_date;
          console.log(`[PowerSync Upload] Income ${id}: enriched partial PATCH with full row data`);
        }
      } catch (e) {
        console.warn('[PowerSync Upload] Failed to read full income row from DB:', e);
      }
    }
    await apiClient.updateTransaction(serverId, {
      type: 'income',
      ...updateData,
    });
  } else if (op === UpdateType.DELETE) {
    if (isUUID(id)) {
      console.log('[PowerSync Upload] Skipping DELETE for UUID income (not yet synced):', id);
      return;
    }
    const serverId = parseInt(id);
    if (isNaN(serverId)) {
      console.warn('[PowerSync Upload] Cannot delete income with non-numeric id:', id);
      return;
    }
    await apiClient.deleteTransaction(serverId, 'income');
  }
}

// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
// 📁 Categories
// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
async function handleCategories(
  apiClient: UploadApiClient,
  op: UpdateType,
  id: string,
  data: Record<string, any>
): Promise<void> {
  if (op === UpdateType.PUT) {
    if (isUUID(id)) {
      // Новая категория, создаём на сервере
      if (data.name) {
        await apiClient.addCategory(data.name, {
          parent_id: data.parent_id != null ? Number(data.parent_id) : undefined,
          emoji: data.emoji || undefined,
          category_level: data.category_level || undefined,
        });
      }
    } else {
      // Серверная запись, не создаём повторно
      console.log('[PowerSync Upload] Skipping CREATE for non-UUID category (already on server):', id);
    }
  } else if (op === UpdateType.PATCH) {
    if (isUUID(id)) {
      console.log('[PowerSync Upload] Skipping UPDATE for UUID category (not yet synced):', id);
      return;
    }

    // Soft-delete: категория помечена на удаление — вызываем серверный API по ID
    if (data._pending_delete) {
      const targetId = data._delete_target_id || undefined;
      if (apiClient.deleteCategoryById) {
        await apiClient.deleteCategoryById(id, targetId);
        console.log('[PowerSync Upload] Category soft-delete by ID processed:', id, targetId ? `→ target ${targetId}` : '');
      } else {
        // Fallback на name-based delete (нужно имя)
        const categoryName = data.name;
        if (categoryName) {
          const numericTarget = targetId ? parseInt(String(targetId)) : undefined;
          await apiClient.deleteCategory(categoryName, numericTarget);
          console.log('[PowerSync Upload] Category soft-delete by name processed:', categoryName);
        } else {
          console.warn('[PowerSync Upload] Category soft-delete failed: no deleteCategoryById and no name, id:', id);
        }
      }
      return;
    }

    // Обновление категории по ID: name, parent_id, emoji.
    // Серверный endpoint: PUT /api/categories/id/:categoryId
    const hasName = data.name != null && String(data.name).trim() !== '';
    const hasParentId = data.parent_id !== undefined;
    const hasEmoji = data.emoji !== undefined;
    if (hasName || hasParentId || hasEmoji) {
      if (apiClient.renameCategoryById) {
        const parentId = hasParentId ? (data.parent_id == null ? null : Number(data.parent_id)) : undefined;
        const emoji = hasEmoji ? data.emoji : undefined;
        await apiClient.renameCategoryById(id, hasName ? String(data.name).trim() : '', parentId, emoji);
      } else if (hasName) {
        if (data.old_name) {
          await apiClient.renameCategory(data.old_name, data.name);
        } else {
          console.warn('[PowerSync Upload] Category PATCH: renameCategoryById not available and no old_name, skipping');
        }
      }
    }
  } else if (op === UpdateType.DELETE) {
    if (isUUID(id)) {
      console.log('[PowerSync Upload] Skipping DELETE for UUID category (not yet synced):', id);
      return;
    }
    const categoryName = data.name || data.category_name;
    if (categoryName) {
      await apiClient.deleteCategory(categoryName);
    } else {
      // PowerSync DELETE CrudEntry не содержит данных строки (opData пуст).
      // Серверное удаление категории выполняется через прямой API-вызов в handleConfirmDeleteCategory.
      console.log('[PowerSync Upload] Category DELETE without name (id:', id,
        ') — server deletion handled via direct API call');
    }
  }
}

// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
// 🎯 Goals
// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
async function handleGoals(
  apiClient: UploadApiClient,
  op: UpdateType,
  id: string,
  data: Record<string, any>
): Promise<void> {
  if (op === UpdateType.PUT) {
    await apiClient.addGoal({
      name: data.name,
      icon: data.icon,
      target_amount: data.target_amount ? Number(data.target_amount) : undefined,
      deadline: data.deadline,
    });
  } else if (op === UpdateType.PATCH) {
    if (isUUID(id)) {
      console.log('[PowerSync Upload] Skipping UPDATE for UUID goal (not yet synced):', id);
      return;
    }

    // Detect close: status changed to CLOSED with closed_at set
    if (data.status === 'CLOSED' && data.closed_at) {
      const paymentMethodId = data.close_payment_method_id || undefined;
      if (apiClient.closeGoal) {
        await apiClient.closeGoal(id, paymentMethodId);
      } else {
        console.warn('[PowerSync Upload] closeGoal not available in apiClient, falling back to updateGoal');
        await apiClient.updateGoal(id, { status: 'CLOSED' });
      }
      return;
    }

    // Detect restore: status changed to ACTIVE with closed_at cleared
    if (data.status === 'ACTIVE' && data.closed_at === null) {
      if (apiClient.restoreGoal) {
        await apiClient.restoreGoal(id);
      } else {
        console.warn('[PowerSync Upload] restoreGoal not available in apiClient, falling back to updateGoal');
        await apiClient.updateGoal(id, { status: 'ACTIVE' });
      }
      return;
    }

    // Regular goal update
    await apiClient.updateGoal(id, {
      name: data.name,
      icon: data.icon,
      target_amount: data.target_amount !== undefined ? Number(data.target_amount) : undefined,
      deadline: data.deadline,
    });
  } else if (op === UpdateType.DELETE) {
    if (isUUID(id)) {
      console.log('[PowerSync Upload] Skipping DELETE for UUID goal (not yet synced):', id);
      return;
    }
    await apiClient.deleteGoal(id);
  }
}

// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
// 📏 Category Limits
// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
async function handleCategoryLimits(
  apiClient: UploadApiClient,
  op: UpdateType,
  id: string,
  data: Record<string, any>,
  getDb?: () => any
): Promise<void> {
  if (op === UpdateType.PUT) {
    const periodMonths = parsePeriodToMonths(data.period);
    await apiClient.setLimit(
      data.category_name || '',
      Number(data.limit_amount) || 0,
      periodMonths,
      data.category_id ? Number(data.category_id) : undefined,
      data.start_month || undefined
    );
  } else if (op === UpdateType.PATCH) {
    // Soft-delete: лимит помечен на удаление — вызываем серверный API
    if (data._pending_delete) {
      if (isUUID(id)) {
        // UUID лимит ещё не синхронизирован — удалять на сервере нечего
        console.log('[PowerSync Upload] Skipping soft-delete for UUID limit (not yet on server):', id);
        return;
      }

      // PowerSync PATCH opData содержит только ИЗМЕНЁННЫЕ поля.
      // category_name и period не меняются при soft-delete, поэтому их нет в opData.
      // Читаем полные данные строки из локальной БД.
      let categoryName = data.category_name;
      let period = data.period;

      if (!categoryName && getDb) {
        try {
          const db = getDb();
          if (db) {
            const rows = await db.getAll(
              'SELECT category_name, period FROM category_limits WHERE id = ?',
              [id]
            );
            const row = rows?.[0] as any;
            if (row) {
              categoryName = row.category_name;
              period = row.period;
              console.log('[PowerSync Upload] Limit data read from local DB:', categoryName, period);
            }
          }
        } catch (e) {
          console.warn('[PowerSync Upload] Failed to read limit from local DB:', e);
        }
      }

      const periodMonths = parsePeriodToMonths(period);
      if (categoryName) {
        try {
          await apiClient.deleteLimit(categoryName, periodMonths);
          console.log('[PowerSync Upload] Limit soft-delete processed:', categoryName, periodMonths);
        } catch (e) {
          // Fallback: устанавливаем amount=0
          console.warn('[PowerSync Upload] deleteLimit failed, trying setLimit with 0:', e);
          await apiClient.setLimit(categoryName, 0, periodMonths);
        }
      } else {
        console.warn('[PowerSync Upload] Limit soft-delete: no category_name available, id:', id);
      }
      return;
    }

    if (isUUID(id)) {
      console.log('[PowerSync Upload] Skipping UPDATE for UUID limit (not yet synced):', id);
      return;
    }

    // PATCH opData содержит только изменённые поля. category_id/category_name часто отсутствуют.
    let categoryName = data.category_name;
    let categoryId = data.category_id;
    let period = data.period;
    let limitAmount = data.limit_amount;
    let startMonth = data.start_month;

    if ((!categoryName || !categoryId) && getDb) {
      try {
        const db = getDb();
        if (db) {
          const rows = await db.getAll(
            'SELECT category_name, category_id, period, limit_amount, start_month FROM category_limits WHERE id = ?',
            [id]
          );
          const row = rows?.[0] as any;
          if (row) {
            categoryName = categoryName ?? row.category_name;
            categoryId = categoryId ?? row.category_id;
            period = period ?? row.period;
            limitAmount = limitAmount ?? row.limit_amount;
            startMonth = startMonth ?? row.start_month;
          }
        }
      } catch (e) {
        console.warn('[PowerSync Upload] Failed to read limit from local DB:', e);
      }
    }

    const periodMonths = parsePeriodToMonths(period);
    await apiClient.setLimit(
      categoryName || '',
      Number(limitAmount) || 0,
      periodMonths,
      categoryId ? Number(categoryId) : undefined,
      startMonth || undefined
    );
  } else if (op === UpdateType.DELETE) {
    if (isUUID(id)) {
      console.log('[PowerSync Upload] Skipping DELETE for UUID limit (not yet synced):', id);
      return;
    }
    // Hard DELETE — opData не содержит category_name.
    // Попытка: если есть deleteLimitById API — используем его
    if ((apiClient as any).deleteLimitById) {
      await (apiClient as any).deleteLimitById(id);
    } else if (data.category_name) {
      const periodMonths = parsePeriodToMonths(data.period);
      try {
        await apiClient.deleteLimit(data.category_name, periodMonths);
      } catch (e) {
        console.warn('[PowerSync Upload] deleteLimit failed, trying setLimit with 0:', e);
        await apiClient.setLimit(data.category_name, 0, periodMonths);
      }
    } else {
      console.warn('[PowerSync Upload] Limit DELETE: no data available (opData empty for DELETE). id:', id,
        '— limit was already soft-deleted via PATCH');
    }
  }
}

// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
// 🏦 Plaid Transactions
// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
async function handlePlaidTransactions(
  apiClient: UploadApiClient,
  op: UpdateType,
  id: string,
  data: Record<string, any>,
  getDb?: () => any
): Promise<void> {
  // Plaid транзакции в основном read-only. Но мы можем обновить категорию.
  if (op === UpdateType.PATCH && apiClient.updatePlaidTransactionCategory) {
    // Поддерживаем оба поля: category (legacy) и user_corrected_category (актуальный столбец)
    const categoryValue = data.user_corrected_category || data.category;
    if (categoryValue) {
      // opData от массового UPDATE (при слиянии категорий) может не содержать plaid_transaction_id.
      // В этом случае читаем его из локальной БД.
      let plaidId = data.plaid_transaction_id;
      if (!plaidId && getDb) {
        try {
          const db = getDb();
          const rows = await db.getAll(
            'SELECT plaid_transaction_id FROM plaid_transactions WHERE id = ?',
            [id]
          );
          plaidId = rows?.[0]?.plaid_transaction_id;
        } catch (e) {
          console.warn('[PowerSync Upload] Failed to read plaid_transaction_id from DB:', e);
        }
      }
      if (!plaidId) {
        console.warn(`[PowerSync Upload] plaid_transaction_id not found for id=${id}, skipping category update`);
        return;
      }
      console.log(`[PowerSync Upload] Plaid category update: ${plaidId} → ${categoryValue}`);
      await apiClient.updatePlaidTransactionCategory(plaidId, categoryValue);
    }
  }
  // PUT и DELETE для plaid транзакций не поддерживаются (они приходят от банка)
}

// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
// 🏦 Plaid Accounts (только custom_name)
// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
async function handlePlaidAccounts(
  apiClient: UploadApiClient,
  op: UpdateType,
  id: string,
  data: Record<string, any>
): Promise<void> {
  // Plaid accounts в основном server-managed. Поддерживаем только переименование (custom_name).
  if (op === UpdateType.PATCH && data.custom_name !== undefined) {
    if (apiClient.renamePlaidAccount) {
      const serverId = parseInt(id);
      if (!isNaN(serverId)) {
        await apiClient.renamePlaidAccount(serverId, data.custom_name || null);
      } else {
        console.warn('[PowerSync Upload] Cannot rename plaid_account with non-numeric id:', id);
      }
    } else {
      console.warn('[PowerSync Upload] renamePlaidAccount not available in apiClient');
    }
  } else {
    console.log(`[PowerSync Upload] plaid_accounts ${getOpName(op)} skipped (server-managed, only custom_name PATCH supported)`);
  }
}

// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
// 🔄 Recurring Income Definitions
// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
async function handleRecurringIncomeDefinitions(
  apiClient: UploadApiClient,
  op: UpdateType,
  id: string,
  data: Record<string, any>,
  getTimezone?: () => string
): Promise<void> {
  console.log(`[RECURRING][UPLOAD_INCOME] op=${op} id=${id} title="${data.title}" freq=${data.frequency}`);
  if (op === UpdateType.PUT) {
    if (!apiClient.addRecurringIncomeDefinition) {
      console.warn('[RECURRING][UPLOAD_INCOME] addRecurringIncomeDefinition not implemented');
      return;
    }
    const deviceTimezone = getTimezone?.() || 'UTC';
    console.log(`[RECURRING][UPLOAD_INCOME] PUT → API addRecurringIncomeDefinition: title="${data.title}" amount=${data.default_amount} freq=${data.frequency} start=${data.start_date} tz=${deviceTimezone}`);
    await apiClient.addRecurringIncomeDefinition({
      title: data.title,
      default_amount: Number(data.default_amount),
      frequency: data.frequency,
      start_date: data.start_date,
      notification_time: data.notification_time,
      timezone: deviceTimezone,
      reminder_times: safeJsonParse(data.reminder_times) || [],
      reminder_offsets: safeJsonParse(data.reminder_offsets) || [0],
      reminders_enabled: !!data.reminders_enabled,
      description: data.description,
      payment_method_id: data.payment_method_id ? Number(data.payment_method_id) : undefined,
      next_notification_date: data.next_notification_date || undefined,
    });
    console.log(`[RECURRING][UPLOAD_INCOME] ✅ PUT completed for id=${id}`);
  } else if (op === UpdateType.PATCH) {
    if (isUUID(id)) {
      console.log('[PowerSync Upload] Skipping UPDATE for UUID recurring income (not yet synced):', id);
      return;
    }
    if (!apiClient.updateRecurringIncomeDefinition) return;
    const serverId = parseInt(id);
    if (isNaN(serverId)) return;

    const updates: Record<string, any> = {};
    if (data.title !== undefined) updates.title = data.title;
    if (data.default_amount !== undefined) updates.default_amount = Number(data.default_amount);
    if (data.frequency !== undefined) updates.frequency = data.frequency;
    if (data.start_date !== undefined) {
      // Сервер ожидает "YYYY-MM-DD", а процессор хранит полный timestamp "2026-03-15 14:36:00Z"
      updates.start_date = String(data.start_date).replace(/[T ].+$/, '');
    }
    if (data.notification_time !== undefined) updates.notification_time = data.notification_time;
    if (data.notification_time !== undefined || data.start_date !== undefined) {
      // При изменении времени/даты обновляем timezone устройства
      updates.timezone = getTimezone?.() || 'UTC';
    }
    if (data.reminder_times !== undefined) updates.reminder_times = safeJsonParse(data.reminder_times);
    if (data.reminder_offsets !== undefined) updates.reminder_offsets = safeJsonParse(data.reminder_offsets);
    if (data.reminders_enabled !== undefined) updates.reminders_enabled = !!data.reminders_enabled;
    if (data.payment_method_id !== undefined) updates.payment_method_id = Number(data.payment_method_id);
    if (data.is_active !== undefined) updates.is_active = !!data.is_active;
    // next_notification_date: мобильный — авторитет, отправляем чтобы сервер хранил актуальное значение
    if (data.next_notification_date !== undefined) updates.next_notification_date = data.next_notification_date;

    // Если нет полей для обновления, пропускаем API вызов
    if (Object.keys(updates).length === 0) {
      console.log(`[RECURRING][UPLOAD_INCOME] PATCH → Пропускаем: нет полей для отправки на сервер (serverId=${serverId})`);
      return;
    }
    console.log(`[RECURRING][UPLOAD_INCOME] PATCH → API updateRecurringIncomeDefinition serverId=${serverId} fields=[${Object.keys(updates).join(', ')}]`);
    await apiClient.updateRecurringIncomeDefinition(serverId, updates);
    console.log(`[RECURRING][UPLOAD_INCOME] ✅ PATCH completed for serverId=${serverId}`);
  } else if (op === UpdateType.DELETE) {
    if (isUUID(id)) {
      console.log('[RECURRING][UPLOAD_INCOME] Skipping DELETE for UUID recurring income (not yet synced):', id);
      return;
    }
    if (!apiClient.deleteRecurringIncomeDefinition) return;
    const serverId = parseInt(id);
    if (isNaN(serverId)) return;
    console.log(`[RECURRING][UPLOAD_INCOME] DELETE → API deleteRecurringIncomeDefinition serverId=${serverId}`);
    await apiClient.deleteRecurringIncomeDefinition(serverId, false);
  }
}

// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
// 📅 Goal Deposit Templates (Шаблоны автопополнений)
// Зеркалит PostgreSQL таблицу goal_deposits
// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
async function handleGoalDepositTemplates(
  apiClient: UploadApiClient,
  op: UpdateType,
  id: string,
  data: Record<string, any>
): Promise<void> {
  if (op === UpdateType.PUT) {
    if (!apiClient.createRecurringGoalDeposit) {
      console.warn('[PowerSync Upload] createRecurringGoalDeposit not implemented');
      return;
    }
    // 🔥 FIX: Передаём timezone устройства чтобы сервер правильно вычислил next_deposit_date
    const deviceTimezone = (() => {
      try { return Intl.DateTimeFormat().resolvedOptions().timeZone; } catch { return undefined; }
    })();
    await apiClient.createRecurringGoalDeposit({
      goal_id: data.goal_id ? Number(data.goal_id) : undefined,
      title: data.title,
      amount: Number(data.amount),
      frequency: data.frequency,
      day_of_week: data.day_of_week != null ? Number(data.day_of_week) : undefined,
      day_of_month: data.day_of_month != null ? Number(data.day_of_month) : undefined,
      start_date: data.start_date,
      notification_time: data.notification_time,
      payment_method_id: data.payment_method_id != null ? Number(data.payment_method_id) : undefined,
      timezone: deviceTimezone,
    });
  } else if (op === UpdateType.PATCH) {
    if (isUUID(id)) {
      console.log('[PowerSync Upload] Skipping UPDATE for UUID goal deposit template (not yet synced):', id);
      return;
    }
    if (!apiClient.updateRecurringGoalDeposit) return;
    const updates: Record<string, any> = {};
    if (data.title !== undefined) updates.title = data.title;
    if (data.amount !== undefined) updates.amount = Number(data.amount);
    if (data.frequency !== undefined) updates.frequency = data.frequency;
    if (data.day_of_week !== undefined) updates.day_of_week = Number(data.day_of_week);
    if (data.day_of_month !== undefined) updates.day_of_month = Number(data.day_of_month);
    if (data.start_date !== undefined) updates.start_date = data.start_date;
    if (data.notification_time !== undefined) updates.notification_time = data.notification_time;
    if (data.next_deposit_date !== undefined) updates.next_deposit_date = data.next_deposit_date;
    if (data.payment_method_id !== undefined) updates.payment_method_id = data.payment_method_id != null ? Number(data.payment_method_id) : null;
    await apiClient.updateRecurringGoalDeposit(id, updates);
  } else if (op === UpdateType.DELETE) {
    if (isUUID(id)) {
      console.log('[PowerSync Upload] Skipping DELETE for UUID goal deposit template (not yet synced):', id);
      return;
    }
    if (!apiClient.deleteRecurringGoalDeposit) return;
    await apiClient.deleteRecurringGoalDeposit(id);
  }
}

// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
// ⚙️ User Settings
// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
async function handleUserSettings(
  apiClient: UploadApiClient,
  op: UpdateType,
  id: string,
  data: Record<string, any>
): Promise<void> {
  // user_settings — одна строка на пользователя, поддерживаем только PATCH
  if (op === UpdateType.PATCH) {
    if (!apiClient.updateSettings) {
      console.warn('[PowerSync Upload] updateSettings not available in apiClient');
      return;
    }

    // Маппим колонки PowerSync → ключи API
    const settingsUpdate: Record<string, any> = {};
    if (data.language_code !== undefined) settingsUpdate.language = data.language_code;
    if (data.currency_code !== undefined) settingsUpdate.currency = data.currency_code;
    if (data.timezone !== undefined) settingsUpdate.timezone = data.timezone;
    if (data.timezone_city !== undefined) settingsUpdate.timezone_city = data.timezone_city;
    if (data.first_name !== undefined) settingsUpdate.first_name = data.first_name;
    if (data.last_name !== undefined) settingsUpdate.last_name = data.last_name;
    if (data.balance_hidden !== undefined) settingsUpdate.balance_hidden = !!data.balance_hidden;
    if (data.biometric_enabled !== undefined) settingsUpdate.biometric_enabled = !!data.biometric_enabled;
    if (data.onboarding_completed !== undefined) settingsUpdate.onboarding_completed = !!data.onboarding_completed;
    if (data.avatar_url !== undefined) settingsUpdate.avatar_url = data.avatar_url;
    if (data.manual_accounts_order !== undefined) settingsUpdate.manual_accounts_order = safeJsonParse(data.manual_accounts_order);
    if (data.plaid_institutions_order !== undefined) settingsUpdate.plaid_institutions_order = safeJsonParse(data.plaid_institutions_order);
    if (data.plaid_accounts_order !== undefined) settingsUpdate.plaid_accounts_order = safeJsonParse(data.plaid_accounts_order);
    if (data.theme !== undefined) settingsUpdate.theme = data.theme;
    if (data.offline_notice_dismissed !== undefined) settingsUpdate.offline_notice_dismissed = !!data.offline_notice_dismissed;
    if (data.widget_accounts_order !== undefined) settingsUpdate.widget_accounts_order = safeJsonParse(data.widget_accounts_order);
    if (data.pending_warning_dismissed !== undefined) settingsUpdate.pending_warning_dismissed = !!data.pending_warning_dismissed;

    if (Object.keys(settingsUpdate).length > 0) {
      await apiClient.updateSettings(settingsUpdate);
    }
  } else {
    // PUT/DELETE для user_settings не поддерживаются (строка управляется сервером)
    console.log(`[PowerSync Upload] user_settings ${getOpName(op)} skipped (only PATCH supported)`);
  }
}

// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
// 🎨 User Category Colors (пользовательские цвета категорий)
// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
async function handleUserCategoryColors(
  apiClient: UploadApiClient,
  op: UpdateType,
  id: string,
  data: Record<string, any>
): Promise<void> {
  const categoryId = data.category_id;
  const color = data.color;

  if (op === UpdateType.PUT || op === UpdateType.PATCH) {
    // CREATE or UPDATE - передаём ID записи для правильной синхронизации
    if (!apiClient.upsertCategoryColor) {
      console.warn('[PowerSync Upload] upsertCategoryColor not available in apiClient');
      return;
    }
    if (categoryId && color) {
      await apiClient.upsertCategoryColor(id, String(categoryId), color);
    }
  } else if (op === UpdateType.DELETE) {
    // DELETE
    if (!apiClient.deleteCategoryColor) {
      console.warn('[PowerSync Upload] deleteCategoryColor not available in apiClient');
      return;
    }
    if (categoryId) {
      await apiClient.deleteCategoryColor(String(categoryId));
    }
  }
}

// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
// 🔧 Helpers
// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
function safeJsonParse(value: any): any {
  if (typeof value === 'string') {
    try {
      return JSON.parse(value);
    } catch {
      return value;
    }
  }
  return value;
}

function cleanUpdateData(data: Record<string, any>): Record<string, any> {
  const cleaned: Record<string, any> = {};
  for (const [key, value] of Object.entries(data)) {
    if (value !== undefined && key !== 'user_id' && key !== 'created_at') {
      cleaned[key] = value;
    }
  }
  return cleaned;
}

function parsePeriodToMonths(period: string | number | undefined): number {
  // Если period — число или числовая строка ("1", "3", "6", "12"), возвращаем напрямую
  if (period !== undefined && period !== null) {
    const num = Number(period);
    if (!isNaN(num) && num > 0) return num;
  }
  // Обратная совместимость со строковыми значениями
  switch (period) {
    case 'monthly': return 1;
    case '3_months': case 'quarterly': return 3;
    case '6_months': case 'semiannual': return 6;
    case 'yearly': case 'annual': return 12;
    default: return 1;
  }
}

// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
// 💳 Payment Methods
// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
async function handlePaymentMethods(
  apiClient: UploadApiClient,
  op: UpdateType,
  id: string,
  data: Record<string, any>
): Promise<void> {
  if (op === UpdateType.PUT) {
    // Создание нового счёта
    if (apiClient.createPaymentMethod) {
      await apiClient.createPaymentMethod(
        data.name || '',
        data.type || 'general',
        Number(data.balance) || 0,
        data.subtype || undefined
      );
    } else {
      console.warn('[PowerSync Upload] createPaymentMethod not available in apiClient');
    }
  } else if (op === UpdateType.PATCH) {
    if (isUUID(id)) {
      console.log('[PowerSync Upload] Skipping UPDATE for UUID payment_method (not yet synced):', id);
      return;
    }

    // Detect soft-delete: is_active changed to 0
    if (data.is_active === 0 || data.is_active === false) {
      if (apiClient.deletePaymentMethod) {
        // targetAccountId нужен при ненулевом балансе — иначе сервер вернёт ошибку
        const targetAccountId = data._transfer_target_id || undefined;
        await apiClient.deletePaymentMethod(id, targetAccountId);
      }
      return;
    }

    // Detect set default
    if (data.is_default !== undefined) {
      if (apiClient.setDefaultPaymentMethod) {
        await apiClient.setDefaultPaymentMethod(id, !!data.is_default);
      }
    }

    // Detect balance update
    if (data.balance !== undefined) {
      if (apiClient.updatePaymentMethodBalance) {
        await apiClient.updatePaymentMethodBalance(id, Number(data.balance));
      }
    }

    // Detect name update
    if (data.name !== undefined) {
      if (apiClient.renamePaymentMethod) {
        await apiClient.renamePaymentMethod(id, data.name);
      }
    }

    // Detect subtype update
    if (data.subtype !== undefined) {
      if (apiClient.updatePaymentMethodType) {
        // API требует и subtype, и name — берём name из data или пустую строку
        await apiClient.updatePaymentMethodType(id, data.subtype, data.name || '');
      }
    }
  } else if (op === UpdateType.DELETE) {
    if (isUUID(id)) {
      console.log('[PowerSync Upload] Skipping DELETE for UUID payment_method (not yet synced):', id);
      return;
    }
    if (apiClient.deletePaymentMethod) {
      await apiClient.deletePaymentMethod(id);
    }
  }
}
